import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import CloseIcon from "@mui/icons-material/Close";
import CollectionsIcon from "@mui/icons-material/Collections";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import orgDataService from "services/orgDataService";
import employeeService from "services/employeeService";
import formsService from "services/formsService";
import { Hub } from "common/models";
import { Employee } from "employee/models";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Moment } from "moment";

type YesNo = "yes" | "no" | "";
type PurchaseType = "Meal" | "Fleet Supplies" | "Other" | "";

interface FormData {
  locationId: number | null;
  employeeId: number | null;
  isBargeReceipt: YesNo;
  isBillable: YesNo;
  purchaseDate: Moment | null;
  tripNumber: string;
  purchaseType: PurchaseType;
  totalAmount: string;
  receiptPictures: File[];
}

interface FormErrors {
  locationId?: string;
  employeeId?: string;
  isBargeReceipt?: string;
  isBillable?: string;
  purchaseDate?: string;
  tripNumber?: string;
  purchaseType?: string;
  totalAmount?: string;
  receiptPictures?: string;
}

const purchaseTypeOptions: PurchaseType[] = ["Meal", "Fleet Supplies", "Other"];

const ReceiptSubmission = () => {
  const navigate = useNavigate();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [locations, setLocations] = useState<Hub[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [formData, setFormData] = useState<FormData>({
    locationId: null,
    employeeId: null,
    isBargeReceipt: "",
    isBillable: "",
    purchaseDate: null,
    tripNumber: "",
    purchaseType: "",
    totalAmount: "",
    receiptPictures: [],
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.locationId) {
      loadEmployeesByHub(formData.locationId);
    } else {
      setEmployees([]);
    }
  }, [formData.locationId]);

  const loadData = async () => {
    try {
      const hubsData = await orgDataService.getAllHubs();
      setLocations(hubsData);
    } catch (error) {
      toast.error("Failed to load form data");
    } finally {
      setIsLoading(false);
    }
  };

  const loadEmployeesByHub = async (hubId: number) => {
    setIsLoadingEmployees(true);
    try {
      const result = await employeeService.searchEmployees({
        hubId: hubId,
        status: "activeOnly",
        pagedSearchOptions: { offset: 0, pageSize: 500 },
        sortName: "lastName",
        sortAsc: true,
      } as any);
      setEmployees(result.results || []);
    } catch (error) {
      toast.error("Failed to load employees");
      setEmployees([]);
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  const getValidationSchema = () => {
    const baseSchema: any = {
      locationId: Yup.number().required("Location is required").nullable(),
      employeeId: Yup.number().required("Employee is required").nullable(),
      isBargeReceipt: Yup.string().required("This field is required"),
      purchaseDate: Yup.date().required("Purchase date is required").nullable(),
      totalAmount: Yup.string().required("Total amount is required"),
    };

    if (formData.isBargeReceipt === "yes") {
      baseSchema.isBillable = Yup.string().required("This field is required");
      baseSchema.tripNumber = Yup.string().required("Trip # is required");
    } else if (formData.isBargeReceipt === "no") {
      baseSchema.purchaseType = Yup.string().required("Purchase type is required");
    }

    return Yup.object().shape(baseSchema);
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    try {
      getValidationSchema().validateSync(formData, { abortEarly: false });
    } catch (error) {
      const validation = error as Yup.ValidationError;
      validation.inner.forEach((err) => {
        if (err.path) {
          newErrors[err.path as keyof FormErrors] = err.message;
        }
      });
    }

    if (formData.receiptPictures.length === 0) {
      newErrors.receiptPictures = "At least one receipt picture is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const selectedLocation = locations.find((l) => l.id === formData.locationId);
    const selectedEmployee = employees.find((e) => e.id === formData.employeeId);
    if (!selectedLocation || !selectedEmployee) {
      toast.error("Please select a valid location and employee");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await formsService.submitReceipt(
        {
          locationId: formData.locationId!,
          locationName: selectedLocation.name,
          employeeId: formData.employeeId!,
          employeeName: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
          employeeNumber: selectedEmployee.employeeNumber,
          isBargeReceipt: formData.isBargeReceipt,
          isBillable: formData.isBillable,
          tripNumber: formData.tripNumber,
          purchaseType: formData.purchaseType,
          purchaseDate: formData.purchaseDate
            ? formData.purchaseDate.format("YYYY-MM-DD")
            : "",
          totalAmount: formData.totalAmount,
        },
        formData.receiptPictures
      );

      if (response.success) {
        toast.success(response.message || "Receipt submitted successfully");
        navigate("/forms");
      } else {
        toast.error(response.message || "Failed to submit receipt");
      }
    } catch (error) {
      toast.error("Failed to submit receipt");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: any) => {
    if (field === "locationId") {
      setFormData({ ...formData, locationId: value, employeeId: null });
      setErrors({ ...errors, locationId: undefined, employeeId: undefined });
    } else if (field === "isBargeReceipt") {
      // Reset branch-specific fields when toggling barge receipt
      setFormData({
        ...formData,
        isBargeReceipt: value,
        isBillable: "",
        tripNumber: "",
        purchaseType: "",
      });
      setErrors({
        ...errors,
        isBargeReceipt: undefined,
        isBillable: undefined,
        tripNumber: undefined,
        purchaseType: undefined,
      });
    } else {
      setFormData({ ...formData, [field]: value });
      if (errors[field as keyof FormErrors]) {
        setErrors({ ...errors, [field]: undefined });
      }
    }
  };

  const handleFilesSelected = (
    files: FileList | null,
    inputRef: React.RefObject<HTMLInputElement>
  ) => {
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files);
    setFormData({ ...formData, receiptPictures: [...formData.receiptPictures, ...newFiles] });
    if (errors.receiptPictures) {
      setErrors({ ...errors, receiptPictures: undefined });
    }
    // Reset input so the same file can be reselected if removed
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    const updated = formData.receiptPictures.filter((_, i) => i !== index);
    setFormData({ ...formData, receiptPictures: updated });
  };

  if (isLoading) {
    return (
      <MDBox p={3}>
        <MDTypography>Loading...</MDTypography>
      </MDBox>
    );
  }

  const showBargeFields = formData.isBargeReceipt === "yes";
  const showNonBargeFields = formData.isBargeReceipt === "no";

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDBox>
          <MDTypography variant="h5" fontWeight="medium">
            Receipt Submission
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Rapid Card receipt submission
          </MDTypography>
        </MDBox>
        <MDButton variant="outlined" color="secondary" onClick={() => navigate("/forms")}>
          Back to Forms
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>
            Form Details
          </MDTypography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.locationId}>
                <InputLabel id="location-label">Location *</InputLabel>
                <Select
                  labelId="location-label"
                  id="locationId"
                  value={formData.locationId || ""}
                  label="Location *"
                  onChange={(e) => handleChange("locationId", e.target.value)}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.locationId && <FormHelperText>{errors.locationId}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                error={!!errors.employeeId}
                disabled={!formData.locationId || isLoadingEmployees}
              >
                <InputLabel id="employee-label">
                  {isLoadingEmployees ? "Loading employees..." : "Employee Name *"}
                </InputLabel>
                <Select
                  labelId="employee-label"
                  id="employeeId"
                  value={formData.employeeId || ""}
                  label={isLoadingEmployees ? "Loading employees..." : "Employee Name *"}
                  onChange={(e) => handleChange("employeeId", e.target.value)}
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.lastName}, {employee.firstName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.employeeId && <FormHelperText>{errors.employeeId}</FormHelperText>}
                {!formData.locationId && !errors.employeeId && (
                  <FormHelperText>Select a location first</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <MDTypography variant="h6" fontWeight="medium" mt={4} mb={2}>
            Receipt
          </MDTypography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.isBargeReceipt}>
                <InputLabel id="barge-label">Is this a Barge Receipt? *</InputLabel>
                <Select
                  labelId="barge-label"
                  id="isBargeReceipt"
                  value={formData.isBargeReceipt}
                  label="Is this a Barge Receipt? *"
                  onChange={(e) => handleChange("isBargeReceipt", e.target.value)}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
                {errors.isBargeReceipt && <FormHelperText>{errors.isBargeReceipt}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>

          {showBargeFields && (
            <Grid container spacing={3} mt={0}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.isBillable}>
                  <InputLabel id="billable-label">Is this Billable? *</InputLabel>
                  <Select
                    labelId="billable-label"
                    id="isBillable"
                    value={formData.isBillable}
                    label="Is this Billable? *"
                    onChange={(e) => handleChange("isBillable", e.target.value)}
                  >
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                  {errors.isBillable && <FormHelperText>{errors.isBillable}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          )}

          {(showBargeFields || showNonBargeFields) && (
            <Grid container spacing={3} mt={0}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Purchase Date *"
                  value={formData.purchaseDate}
                  onChange={(value) => handleChange("purchaseDate", value)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.purchaseDate,
                      helperText: errors.purchaseDate,
                    },
                  }}
                />
              </Grid>

              {showBargeFields && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Trip # *"
                    value={formData.tripNumber}
                    onChange={(e) => handleChange("tripNumber", e.target.value)}
                    error={!!errors.tripNumber}
                    helperText={errors.tripNumber}
                  />
                </Grid>
              )}

              {showNonBargeFields && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.purchaseType}>
                    <InputLabel id="purchase-type-label">Purchase Type *</InputLabel>
                    <Select
                      labelId="purchase-type-label"
                      id="purchaseType"
                      value={formData.purchaseType}
                      label="Purchase Type *"
                      onChange={(e) => handleChange("purchaseType", e.target.value)}
                    >
                      {purchaseTypeOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.purchaseType && <FormHelperText>{errors.purchaseType}</FormHelperText>}
                  </FormControl>
                </Grid>
              )}
            </Grid>
          )}

          {(showBargeFields || showNonBargeFields) && (
            <>
              <Grid container spacing={3} mt={0}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Total Amount *"
                    type="number"
                    value={formData.totalAmount}
                    onChange={(e) => handleChange("totalAmount", e.target.value)}
                    error={!!errors.totalAmount}
                    helperText={errors.totalAmount}
                    inputProps={{
                      inputMode: "decimal",
                      min: 0,
                      step: "0.01",
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mt={0}>
                <Grid item xs={12}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Receipt Pictures *
                  </MDTypography>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: "none" }}
                    onChange={(e) => handleFilesSelected(e.target.files, cameraInputRef)}
                  />
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => handleFilesSelected(e.target.files, galleryInputRef)}
                  />
                  <Stack direction="row" spacing={1}>
                    <MDButton
                      variant="outlined"
                      color="info"
                      startIcon={<PhotoCameraIcon />}
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      Take Photo
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="info"
                      startIcon={<CollectionsIcon />}
                      onClick={() => galleryInputRef.current?.click()}
                    >
                      Choose Photos
                    </MDButton>
                  </Stack>
                  {formData.receiptPictures.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" mt={2} useFlexGap>
                      {formData.receiptPictures.map((file, index) => (
                        <Chip
                          key={`${file.name}-${index}`}
                          label={file.name}
                          onDelete={() => handleRemoveFile(index)}
                          deleteIcon={<CloseIcon />}
                        />
                      ))}
                    </Stack>
                  )}
                  {errors.receiptPictures && (
                    <FormHelperText error>{errors.receiptPictures}</FormHelperText>
                  )}
                </Grid>
              </Grid>
            </>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
            <MDButton variant="outlined" color="secondary" onClick={() => navigate("/forms")}>
              Cancel
            </MDButton>
            <MDButton
              variant="contained"
              color="dark"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </MDButton>
          </Box>
        </MDBox>
      </Card>
    </MDBox>
  );
};

export default ReceiptSubmission;
